import { h, type ComputedRef, type VNode } from 'vue'
import type { EventDescription } from '~/composables/types/event'
import { HITS_PER_PAGE } from './helpers'

interface PanelRenderOptions {
  elements: Record<string, any>
  state: any
  setIsOpen: (open: boolean) => void
  lastViewedEvents: ComputedRef<EventDescription[]>
  localePath: (path: string) => string
  t: (key: string) => string
}

/**
 * Checks whether the Algolia events source returned actual results
 * by inspecting the rendered VNode structure.
 *
 * TODO: Replace with a reactive ref tracking result count from
 * transformResponse — this className check is fragile.
 */
const hasEventResults = (eventResults: any): boolean =>
  eventResults?.children?.[1]?.props?.className !== 'aa-SourceNoResults'

export const createPanelLayout = ({
  elements,
  state,
  setIsOpen,
  lastViewedEvents,
  localePath,
  t,
}: PanelRenderOptions): VNode => {
  const {
    eventCategories = null,
    events = null,
    locations = null,
  } = elements || {}

  const areThereCityOrCategoryResults =
    Boolean(locations) || Boolean(eventCategories)
  const shouldShowAllResultsButton =
    areThereCityOrCategoryResults || hasEventResults(events)
  const shouldShowLastViewedEvents =
    (lastViewedEvents.value?.length ?? 0) > 0 && !state?.query

  return h('div', { class: 'aa-PanelLayout aa-Panel--scrollable' }, [
    h('div', { class: 'aa-PanelSections' }, [
      createLeftColumn(locations, eventCategories),
      createEventsColumn(
        events,
        areThereCityOrCategoryResults,
        shouldShowLastViewedEvents,
        shouldShowAllResultsButton,
        state,
        localePath,
        t
      ),
      createLastViewedColumn(
        shouldShowLastViewedEvents,
        lastViewedEvents.value,
        setIsOpen,
        localePath,
        t
      ),
    ]),
  ])
}

const createLeftColumn = (locations: any, eventCategories: any): VNode =>
  h('div', { class: 'aa-PanelSection--left' }, [
    h('div', null, locations),
    h('div', { class: 'aa-CategorySection' }, eventCategories),
  ])

const createEventsColumn = (
  events: any,
  areThereCityOrCategoryResults: boolean,
  shouldShowLastViewedEvents: boolean,
  shouldShowAllResultsButton: boolean,
  state: any,
  localePath: (path: string) => string,
  t: (key: string) => string
): VNode =>
  h(
    'div',
    {
      class:
        'aa-PanelSection--right ' +
        (areThereCityOrCategoryResults ? 'one-column' : 'two-columns') +
        (shouldShowLastViewedEvents ? ' hidden' : ''),
    },
    [
      events,
      h(
        'div',
        {
          class: 'flex ' + (shouldShowAllResultsButton ? '' : 'hidden'),
        },
        [
          h(
            'a',
            {
              href: localePath(
                `/search/${state?.query ? '?searchquery=' + state?.query : ''}`
              ),
              class:
                'block w-full text-center mt-8 mb-4 ml-2 px-4 py-3 rounded-lg mx-auto text-white bg-teal-900 cursor-pointer',
            },
            t('general.showAllResults')
          ),
        ]
      ),
    ]
  )

const createLastViewedColumn = (
  shouldShow: boolean,
  events: EventDescription[],
  setIsOpen: (open: boolean) => void,
  localePath: (path: string) => string,
  t: (key: string) => string
): VNode =>
  h(
    'div',
    {
      class: 'aa-PanelSection--right ml-4 ' + (!shouldShow ? ' hidden' : ''),
    },
    [
      h('div', { class: 'flex my-4' }, [
        h(
          'span',
          { class: 'aa-SourceHeaderTitle shrink-0' },
          t('home.lastViewedEvents')
        ),
      ]),
      ...(events
        ?.slice(0, HITS_PER_PAGE.lastViewed)
        ?.map((event: EventDescription) =>
          h(
            'div',
            {
              class: 'aa-ItemContent',
              onClick: () => {
                setIsOpen(false)
                window.location.href = localePath(`/e/${event.permalink}`)
              },
            },
            [
              h('img', {
                class: 'w-20 h-16 mb-2 object-cover mr-2 rounded-md',
                src: event?.images?.[0]?.thumb,
                alt: event?.label,
              }),
              h('span', { class: 'aa-ItemContentTitle' }, event?.title),
            ]
          )
        ) || []),
    ]
  )
